/* eslint-disable max-len */

import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { NavigateFunction } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import ErrorMessage, { ErrorObject } from '../../components/ErrorModal';
import ValidateMeiaEntrada from '../../components/ValidateMeiaEntrada';
import ValidateMembership from '../../components/ValidateMembership';
import { Environment } from '../../constants/environments';
import { getErrorMessage } from '../../constants/errors';
import { HttpVerb } from '../../constants/httpVerbs';
import { avatarDefaultUrl } from '../../constants/images';
import { Producers } from '../../constants/producers';
import { TicketType as TicketTypes } from '../../constants/tickets';
import { AssignmentTickets, AssignTicketToHolderProps, AssociateTheTicketForMeProps, CheckoutComboDataProps, CheckoutDataProps, ComboInCart, ComboInNaming, ErrorValidating, HolderProps, SummaryData, TicketsPerSession } from '../../types/cart';
import { Checkout, ComboCheckout } from '../../types/checkout';
import { Event } from '../../types/event';
import { ResponseGateway, GatewayDetails } from '../../types/gateway';
import { ComboDate, Ticket, TicketRestrictions, TicketType } from '../../types/ticket';
import { User } from '../../types/user';
import { createErrorHandlers } from '../../utils/errorHandlers';
import getIngCookieValue from '../../utils/getIngCookies';
import { logger } from '../../utils/logger';
import removeDocumentMask from '../../utils/removeDocumentMask';
import { useApiService } from '../useApiService';
import useGuardian from '../useGuardian';
import useRules from '../useRules';
import { ComboData } from './comboProgress.context';
import { useEventContext } from './event.context';
import { useI18nContext } from './i18n.context';
import { useLoaderScreenContext } from './loader-screen.context';
import { useMembershipContext } from './membership.context';
import { useModalContext } from './modal.context';
import { useUserContext } from './user.context';

export type CartContextProps = {
  combosInNaming: ComboInNaming[];
  updateCombosInNaming: (updatedCombosInNaming : ComboInNaming[]) => void;
  combosInCart: ComboInCart[];
  ticketsPerSession: TicketsPerSession;
  blockedSessionsForMe: number[];
  cart: Ticket[];
  cartLoading: boolean;
  totalTicketsInCart: number;
  summaryData: SummaryData[];
  namingData: AssignmentTickets[];
  errorValidating: ErrorValidating;
  transactionId: string | null;
  paymentType: string | null;
  validateCart: ({
    user,
    event,
    navigate
  }: {
    user: User;
    event: Event;
    navigate: NavigateFunction
  }) => Promise<boolean>;
  prepareCheckoutData: ({
    user,
    event,
    namingData,
  }: CheckoutDataProps) => Checkout;
  getTotalTicketsInCart: () => number;
  associateTheTicketForMe: ({
    idSpecificInIdentifyList,
    user,
  }: AssociateTheTicketForMeProps) => string;
  invalidateAssignTicketToHolder: ({ idSpecificInIdentifyList }: { idSpecificInIdentifyList: string; }) => void;
  assignTicketToHolder: ({
    idSpecificInIdentifyList,
    holder,
  }: AssignTicketToHolderProps) => void;
  removeSelectedTickets: (params: { ticketId: number, definitiveSessionId: string }) => void;
  addTicketToCart: (params: { groupId: number; ticketId: number, restrictions: TicketRestrictions }) => void;
  removeTicketFromCart: (params: { groupId: number; ticketId: number, restrictions: TicketRestrictions }) => void;
  checkQuantityIfAddedInCart: (params: {
    groupId: number;
    ticketId: number;
  }) => number;
  getTotalTicketsByGroup: (params: { groupId: number }) => number;
  requestCheckoutPaymentUrl: (
    checkoutData: Checkout
  ) => Promise<GatewayDetails | undefined>;
  updateNamingDataList: (namingDataList: AssignmentTickets[]) => void;
  updateBlockedSessionsForMe: (namingDataList: AssignmentTickets[], isRemove?: boolean) => void;
  updateTransactionId: (id: string) => void;
  updatePaymentType: (paymentType: string | null) => void;
  resetCart: (preserveTransactionId?: boolean) => void;
  updateComboInCart: (combo: ComboData, remove?: boolean) => void;
  createComboInNaming: () => void;
  prepareCheckoutComboData: (checkoutComboDataProps: CheckoutComboDataProps) => ComboCheckout;
};

const CartContext = createContext<CartContextProps>({} as CartContextProps);

function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<Ticket[]>([]);
  const [namingData, setNamingData] = useState<AssignmentTickets[]>([]);
  const [summaryData, setSummaryData] = useState<SummaryData[]>([]);
  const [totalTicketsInCart, setTotalTicketsInCart] = useState(0);
  const [errorValidating, setErrorValidating] = useState<ErrorValidating>({
    code : null,
    error : null,
  });
  const [blockedSessionsForMe, setBlockedSessionsForMe] = useState<number[]>([]);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [paymentType, setPaymentType] = useState<string | null>(null);
  const [ticketsPerSession, setTicketsPerSession] = useState<TicketsPerSession>({} as TicketsPerSession);
  const [combosInCart, setCombosInCart] = useState<ComboInCart[]>([]);
  const [combosInNaming, setCombosInNaming] = useState<ComboInNaming[]>([]);
  const [cartLoading, setCartLoading] = useState(false);

  const { user, handleAuthenticatedUser, userHasBiometry } = useUserContext();

  const { translate } = useI18nContext();
  const {
    event,
    tickets,
    producerId,
    couponApplied,
    passKeyApplied,
    listOfDependents,
    updateDependentList,
    getTicketsInMyWallet,
    handleSetErrorsInSummary,
    selectedIdDateCarousel,
    dataCarousel,
    getGetTickets,
    signatureOGloboApplied
  } = useEventContext();

  const {
    currentLocale : { locale },
  } = useI18nContext();
  const { applyCartRules } = useRules();
  const { request } = useApiService();
  const { toggleModal, defineTitleModal, setContentModal } = useModalContext();

  const { toggleLoader } = useLoaderScreenContext();
  const { navigateToFirstRoute } = useGuardian();

  const { changeMembershipApplied, membershipApplied, verifyHalfEntry } = useMembershipContext();

  const removeTicketsFromTicketsPerSession = (definitiveSessionId: string, quantity: number) => {
    setTicketsPerSession((state) => ({
      ...state,
      [definitiveSessionId] : state[definitiveSessionId] - quantity,
    }));
  };

  /**
   * Removes the selected tickets from the cart based on the provided ticket ID.
   * It filters out the ticket types that match the given ticket ID and updates the cart state.
   * @function removeSelectedTickets
   * @param {Object} params - The parameters for removing selected tickets.
   * @param {number} params.ticketId - The ID of the ticket to be removed.
   */
  const removeSelectedTickets = ({
    ticketId,
    definitiveSessionId
  }: { ticketId: number, definitiveSessionId: string }) => {
    let quantityToRemoveFromTicketsPerSession = 0;
    const newCart = cart
      .map((ticket) => {
        const newTypes = ticket.type.filter((curTicket) => {
          if (curTicket.id === ticketId) {
            quantityToRemoveFromTicketsPerSession = curTicket.quantityInCart;
          }

          return curTicket.id !== ticketId;
        });
        return { ...ticket, type : newTypes };
      })
      .filter(({ type }) => type.length > 0);

    setCart(newCart);
    prepareSummaryData({ newCart });
    removeTicketsFromTicketsPerSession(
      definitiveSessionId,
      quantityToRemoveFromTicketsPerSession,
    );
  };

  /**
   * Searches for a group in the available tickets by its ID.
   * Finds and returns the group object from the available tickets that matches the provided groupId.
   * @param groupId - The ID of the group to search for in the available tickets.
   * @returns The group object if found, otherwise undefined.
   */
  const searchForGroupInTickets = (groupId: number) =>
    tickets.find(({ id }) => id === groupId);

  /**
   * Searches for a ticket within a specific group by its ID.
   * Finds and returns the ticket object from the provided ticket group that matches the provided ticketId.
   * @param ticketGroup - The group of tickets to search within.
   * @param ticketId - The ID of the ticket to search for in the group.
   * @returns The ticket object if found, otherwise undefined.
   */
  const searchTicketInGroup = (ticketGroup: Ticket, ticketId: number) => {
    return ticketGroup.type.find(({ id }) => id === ticketId);
  };

  /**
   * Searches for a group in the cart by its ID.
   * Finds and returns the group object from the cart that matches the provided groupId.
   * @param groupId - The ID of the group to search for in the cart.
   * @returns The group object if found, otherwise undefined.
   */
  const searchForGroupInCart = (groupId: number) =>
    cart.find(({ id }) => id === groupId);

  /**
   * Calculates the total number of tickets in the cart for a specific group.
   * Searches for the group in the cart by the provided groupId and sums the quantities of all ticket types within that group.
   * @param groupId - The ID of the group to calculate the total number of tickets for.
   * @returns The total number of tickets in the specified group, or 0 if the group is not found.
   */
  const getTotalTicketsByGroup = ({ groupId }: { groupId: number }): number => {
    const group = searchForGroupInCart(groupId);
    return group ? group.type.reduce(
      (total, { quantityInCart }) => total + quantityInCart,
      0
    ) : 0;
  };

  /**
   * Calculates the total number of tickets in the cart.
   * Sums the quantities of all ticket types across all tickets in the cart.
   * @returns The total number of tickets in the cart.
   */
  const getTotalTicketsInCart = (): number => {
    const totalCartWithoutCombo = cart.reduce((total, ticket) => {
      return (
        total +
        ticket.type.reduce(
          (typeTotal, { quantityInCart }) => typeTotal + quantityInCart,
          0
        )
      );
    }, 0);

    const totalCartJustCombo = combosInCart.reduce((acc, combo) => (
      (acc + combo.comboDates.length) * combo.quantity
    ),
    0,
    );

    return totalCartWithoutCombo + totalCartJustCombo;
  };

  /**
   * Prepares the summary data based on the current state of the cart.
   * Transforms the cart data into a summarized format suitable for display.
   * Maps each ticket and its types to a summary object, which includes information such as
   * ticket ID, price, accumulated value in cart, availability of discounts, group name, title, date, and quantity.
   * @param newCart - The updated array of tickets in the cart.
   */
  const prepareSummaryData = ({ newCart }: { newCart: Ticket[] }) => {
    const expectedSummaryData = newCart.flatMap((ticket) => {
      return ticket.type.map(
        ({
          id,
          accumulatedValueInCart,
          price,
          tagType,
          originalPrice,
          name,
          quantityInCart,
          category,
          tax,
        }) => {
          return {
            id,
            price,
            originalPrice,
            accumulatedValueInCart,
            discountedTicketAvailable : !!tagType,
            group : ticket.name,
            title : name,
            category,
            date : ticket.type[0].dates[0].datetime,
            quantity : quantityInCart,
            tax,
            definitiveSessionId : ticket.type[0].dates.map((date) => date.id).join(''),
          };
        }
      );
    });

    setSummaryData(expectedSummaryData);
  };

  const updateTicketsPerSession = (add: number) => {
    const { definitiveSessionId } = dataCarousel.find((item) => item.id === selectedIdDateCarousel)!;

    setTicketsPerSession((state) => ({
      ...state,
      [definitiveSessionId] : state[definitiveSessionId] ? (
        state[definitiveSessionId] + add
      ) : add,
    }));
  };

  /**
   * Adds a specific ticket to the cart.
   * Searches for the group and ticket in the available tickets, then adds the ticket to the cart.
   * If the group is not already in the cart, it adds the group with the ticket.
   * If the ticket is already in the cart, it increments the quantity.
   * Applies coupon rules if a coupon is applied and updates the cart state and summary data.
   * @param groupId - The ID of the group to which the ticket belongs.
   * @param ticketId - The ID of the ticket to be added to the cart.
   * @param restrictions - Object that returns the minimum and maximum quantity for ticket purchase.
   */
  const addTicketToCart = ({
    groupId,
    ticketId,
    restrictions,
  }: {
    groupId: number;
    ticketId: number;
    restrictions: TicketRestrictions;
  }) => {
    const minMaxEquals = restrictions.minimum === restrictions.maximum;

    const group = searchForGroupInTickets(groupId);
    if (!group) return;

    const ticket = searchTicketInGroup(group, ticketId);
    if (!ticket) return;

    const updateCartWithTicket = () => {
      const cartCopy = [...cart];
      const groupInCart = cartCopy.find(({ id }) => id === groupId);

      const ticketData = {
        ...ticket,
        quantityInCart : minMaxEquals ? restrictions.minimum : 1,
        accumulatedValueInCart : (minMaxEquals ? restrictions.minimum : 1) * ticket.price,
        checkoutPrice : [ticket.price],
        ...(couponApplied && { couponId : couponApplied.coupon.id }),
      };

      if (!groupInCart) {
        return [
          ...cartCopy,
          {
            ...group,
            ...(passKeyApplied && { passkey : passKeyApplied }),
            type : [ticketData],
          },
        ];
      }

      const ticketIndex = groupInCart.type.findIndex(({ id }) => id === ticketId);
      if (ticketIndex === -1) {
        groupInCart.type.push(ticketData);
        return cartCopy;
      }

      const currentTicket = groupInCart.type[ticketIndex];
      const quantityInCart = currentTicket.quantityInCart + 1;

      groupInCart.type[ticketIndex] = {
        ...currentTicket,
        quantityInCart,
        accumulatedValueInCart : currentTicket.price * quantityInCart,
        checkoutPrice : [...currentTicket.checkoutPrice, currentTicket.price],
      };

      return cartCopy;
    };

    const newCart = updateCartWithTicket();

    applyCartRules({
      event : event!,
      tickets,
      cart : newCart,
      couponApplied,
      passKeyApplied,
      updateCart : setCart,
      updateSummaryData : prepareSummaryData,
      membership : membershipApplied,
      updateTicketsPerSession : removeTicketsFromTicketsPerSession,
    });

    updateTicketsPerSession(1);
  };

  /**
   * Removes a specific ticket from the cart.
   * Decreases the quantity of the specified ticket in the cart by 1 if the quantity is greater than 1,
   * or removes the ticket from the cart if the quantity is 1. If the group has no more tickets, it removes the group.
   * Applies coupon rules if a coupon is applied and updates the cart state and summary data.
   * @param groupId - The ID of the group from which the ticket should be removed.
   * @param ticketId - The ID of the ticket to be removed from the cart.
   * @param restrictions- Object that returns the minimum and maximum quantity for ticket purchase.
   */
  const removeTicketFromCart = ({
    groupId,
    ticketId,
    restrictions,
  }: {
    groupId: number;
    ticketId: number;
    restrictions?: TicketRestrictions;
  }) => {
    const minMaxEquals = restrictions?.minimum === restrictions?.maximum;

    const updatedCart = cart
      .map((item) => {
        if (item.id !== groupId) {
          return item;
        }

        const updatedTypes = item.type.filter((cartItem) => {
          const quantity = cartItem.quantityInCart;
          const restrictionsMinimum = restrictions?.minimum || 0;

          if (cartItem.id === ticketId && minMaxEquals) {
            cartItem.quantityInCart = quantity - restrictionsMinimum;
            return cartItem.quantityInCart > 0;
          }

          if (cartItem.id === ticketId && quantity > 1) {
            cartItem.quantityInCart = quantity - 1;
            return true;
          }

          return cartItem.id !== ticketId;
        });

        return { ...item, type : updatedTypes };
      })
      .filter((item) => item.type.length > 0);

    applyCartRules({
      event : event!,
      tickets,
      cart : updatedCart,
      couponApplied,
      passKeyApplied,
      updateCart : setCart,
      updateSummaryData : prepareSummaryData,
      membership : membershipApplied,
      updateTicketsPerSession : removeTicketsFromTicketsPerSession,
    });

    updateTicketsPerSession(-1);
  };

  /**
   * Checks the quantity of a specific ticket in the cart.
   * Searches for the group in the cart by the provided groupId, then finds the ticket by ticketId within that group,
   * and returns the quantity of that ticket in the cart. If the group or ticket is not found, returns 0.
   * @param groupId - The ID of the group to search for in the cart.
   * @param ticketId - The ID of the ticket to search for within the group.
   * @returns The quantity of the specified ticket in the cart, or 0 if not found.
   */
  const checkQuantityIfAddedInCart = ({
    groupId,
    ticketId,
  }: {
    groupId: number;
    ticketId: number;
  }): number => {
    const group = searchForGroupInCart(groupId);
    if (!group) return 0;
    const ticket = group.type.find(({ id }) => id === ticketId);
    return ticket ? ticket.quantityInCart : 0;
  };

  /**
   * Creates a deep clone of the given object.
   * This function takes an object and returns a deep copy of it by serializing and deserializing
   * the object using JSON. This ensures that all nested objects and arrays are also cloned.
   * @template T - The type of the input object.
   * @param The object to be cloned.
   * @returns A deep clone of the input object.
   */
  function deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * Updates the prices in the cart if a coupon is applied.
   * This function creates a deep clone of the cart array and updates the prices of the ticket types
   * within the cloned cart based on the prices of the corresponding ticket types in the tickets array.
   * If a ticket type in the cart has the same ID as a ticket type in the tickets array, its price is
   * updated to match the price in the tickets array.
   * @returns A deep clone of the cart array with updated prices for ticket types where a coupon is applied.
   */
  const updatePriceInCartIfCouponApplied = () => {
    const copyCart = cart.map((ticket) => deepClone(ticket));

    copyCart.forEach((copyTicket) => {
      copyTicket.type.forEach((ticketType) => {
        const idRef = ticketType.id;

        tickets.forEach((ticket) => {
          ticket.type.forEach((type) => {
            if (type.id === idRef) {
              ticketType.price = type.price;
              ticketType.tagType = type.tagType;
            }
          });
        });
      });
    });

    applyCartRules({
      event : event!,
      tickets,
      cart : copyCart,
      couponApplied,
      passKeyApplied,
      updateCart : setCart,
      updateSummaryData : prepareSummaryData,
      membership : membershipApplied,
      updateTicketsPerSession : removeTicketsFromTicketsPerSession,
    });
  };

  const invalidateAssignTicketToHolder = ({ idSpecificInIdentifyList }: { idSpecificInIdentifyList: string; }) => {
    const expectedNamingData = namingData.map((ticket) => {
      if (ticket.idSpecificInIdentifyList === idSpecificInIdentifyList) {
        updateDependentList({
          dependents : listOfDependents.map((dependent) => {
            if (ticket.holder && dependent.email === ticket.holder.email) {
              return { ...dependent, hasTicket : false };
            }
            return dependent;
          }),
        });

        return {
          ...ticket,
          isPassport : ticket.dates.length > 1,
          holder : null,
        };
      }
      return ticket;
    });

    setNamingData(expectedNamingData);
    updateBlockedSessionsForMe(expectedNamingData, true);
  };

  const assignTicketToHolder = ({
    idSpecificInIdentifyList,
    holder,
  }: AssignTicketToHolderProps) => {
    const expectedNamingData = namingData.map((ticket) => {
      if (ticket.idSpecificInIdentifyList === idSpecificInIdentifyList) {
        return { ...ticket, isPassport : ticket.dates.length > 1, holder };
      }
      return ticket;
    });

    setNamingData(expectedNamingData);
    // justOneAssignPerSession(expectedNamingData);
    updateBlockedSessionsForMe(expectedNamingData);
  };

  /**
   * Associates a ticket with a user by assigning the ticket to the holder.
   *
   * This function takes an object containing a unique identifier for the ticket
   * and a user object, creates a holder object with the user's information, and
   * assigns the ticket to this holder. It then returns the holder's email address.
   * @param {number} params.idSpecificInIdentifyList - The unique identifier for the ticket.
   * @param {User} params.user - The user object to associate with the ticket.
   *
   **/
  const associateTheTicketForMe = ({
    idSpecificInIdentifyList,
    user,
  }: AssociateTheTicketForMeProps): string => {
    const holder: HolderProps = {
      id : user.id,
      name : user.name,
      email : user.email,
      picture : user.pictures?.[0].link ?? '',
      faceRecognition : userHasBiometry,
    };
    assignTicketToHolder({ idSpecificInIdentifyList, holder });
    return holder.email;
  };

  /**
   * Prepares tickets for assignment by transforming the tickets in the cart into a new format.
   * This function processes the `cart` array, extracting and transforming ticket information
   * from each group and each ticket within the group. It generates a new array of tickets
   * with additional properties and unique identifiers.
   * @returns AssignmentTickets[] An array of transformed tickets ready for assignment.
   * @property {number} idSpecificInIdentifyList - A unique identifier within the identification list.
   * @property {string} holder - The holder of the ticket.
   */
  const prepareTicketsForAssignment = (): AssignmentTickets[] => {
    return cart.flatMap((group) => {
      return group.type.flatMap((ticket) => {
        return Array.from(
          { length : ticket.quantityInCart },
          () => ticket
        ).flatMap((ticket, ticketIndex) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { quantityInCart, accumulatedValueInCart, tagType, canSell, checkoutPrice, originalPrice,
            ...rest
          } = ticket;
          const uniqueId = uuidv4();
          return {
            ...rest,
            groupId : group.id,
            isPassport : ticket.dates.length > 1,
            holder : null,
            price : checkoutPrice[ticketIndex % ticket.checkoutPrice.length] || ticket.price,
            idSpecificInIdentifyList : uniqueId,
            faceRecognition : group.faceRecognition,
            transferMandatoryFaceRecognition :
              group.transferMandatoryFaceRecognition,
            groupName : group.name,
          };
        });
      });
    });
  };

  /**
   * Prepares the checkout data for an event.
   * @param {Object} params - The parameters for the function.
   * @param {User} params.user - The user making the checkout.
   * @param {Event} params.event - The event details.
   * @param {Array} params.namingData - The ticket naming data.
   * @returns Checkout The prepared checkout data.
   */
  const prepareCheckoutData = ({
    user,
    event,
    namingData,
  }: CheckoutDataProps): Checkout => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const myTickets = namingData.reduce((acc: TicketType[] | any, ticket) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const existingTicket = acc.find((t: any) => t.guestTypeId === ticket.id);

      if (existingTicket) {
        existingTicket.quantity += 1;
        ticket.holder && existingTicket.holder.push(ticket.holder!);
      } else {
        acc.push({
          quantity : 1,
          price : ticket.price,
          guestTypeId : ticket.id,
          holder : [ticket.holder!],
          ...(couponApplied && ticket.hasCoupon && { couponId : String(couponApplied.coupon.id) }),
        });
      }
      return acc;
    }, []);
    const data: Checkout = {
      eventTitle : event.title,
      eventId : event.id,
      userId : user.id,
      socialmedia : '',
      tickets : myTickets,
      ...(passKeyApplied && { passkey : passKeyApplied }),
      producerId : event.producerId,
      document : user.document?.number,
      cpfGlobo : signatureOGloboApplied ?? undefined,
    };

    return data;
  };

  const prepareCheckoutComboData = ({
    user,
    event,
    comboNamingData,
  }: CheckoutComboDataProps): ComboCheckout => {
    const makeComboPrice = (comboDate: ComboDate) => {
      const value = comboDate.groups[0].tickets[0].price;
      return (value - (value * comboDate.discount) / 100);
    };

    const hasValidUser = (): boolean => {
      return Boolean(
        user &&
        typeof user.name === 'string' &&
        user.name.trim().length > 0 &&
        typeof user.email === 'string' &&
        user.email.includes('@') &&
        user.email.trim().length > 0
      );
    };

    const getValidatedHolder = (combo: ComboInNaming): HolderProps => {
      if (combo.holder) {
        return {
          id : combo.holder.id,
          email : combo.holder.email,
          name : combo.holder.name,
          picture : combo.holder.picture,
        };
      }

      if (hasValidUser()) {
        return {
          id : user.id,
          email : user.email!.trim(),
          name : user.name!.trim(),
          picture : user.pictures?.[0]?.link ?? avatarDefaultUrl,
        };
      }

      logger.warn('[prepareCheckoutComboData] Invalid user data for holder fallback');

      return {
        id : 0,
        email : '',
        name : '',
        picture : avatarDefaultUrl,
      };
    };

    const comboToTicketsFormat = (combo: ComboInNaming) => {
      return combo.comboDates.map((comboDate) => ({
        quantity : 1, // Always 1 for combos, as duplication happens when multiple combos are added
        comboId : Number(combo.id),
        price : makeComboPrice(comboDate),
        guestTypeId : comboDate.groups[0].tickets[0].id,
        holder : [getValidatedHolder(combo)],
      }));
    };

    const data = {
      eventTitle : event.title,
      eventId : event.id,
      userId : user.id,
      socialmedia : '',
      tickets : comboNamingData.flatMap((combo) => comboToTicketsFormat(combo)),
      ...(passKeyApplied && { passkey : passKeyApplied }),
      producerId : event.producerId,
      document : user.document?.number,
    };
    return data;
  };

  /**
   * Requests the checkout payment URL.
   * @param {Checkout} checkoutData - The data required for the checkout process.
   * @returns {Promise<GatewayDetails | undefined>} A promise that resolves to the gateway details or undefined if an error occurs.
   */
  const requestCheckoutPaymentUrl = async (
    checkoutData: Checkout
  ): Promise<GatewayDetails | undefined> => {
    const apiKey = Environment.API_KEY;
    const userToken = getIngCookieValue('token')!;
    const resourcePath = `shop?apikey=${apiKey}&usertoken=${userToken}`;

    const defaultHolder: HolderProps = {
      id : user?.id || 0,
      name : user?.name || '',
      email : user?.email || '',
      picture : user?.pictures?.[0].link ?? '',
    };

    checkoutData.tickets.forEach((ticket) => {
      if (ticket.holder[0]) return;
      ticket.holder[0] = defaultHolder;
    });

    // Add faceRecognition = true to holders when user has biometry registered and ticket is pre-assigned
    checkoutData.tickets.forEach((ticket) => {
      ticket.holder.forEach((holder) => {
        if (holder.id === user?.id && userHasBiometry) {
          holder.faceRecognition = true;
        }

        if (holder.tokenFaceRecognition) {
          holder.faceRecognition = !holder.hasTicketAssociated;
        }
      });
    });

    try {
      const { responseData } = await request<Checkout, ResponseGateway>({
        requestOptions : { resourcePath, data : checkoutData },
        type : HttpVerb.POST,
      },
      { baseUrl : Environment.API_GATEWAY_URL, needToken : true });

      return responseData.data;
    } catch (error) {
      logger.error(error as string);
      const data = error as { response: { data: { code: string } } };
      const { data : errorObject } = data.response;

      defineTitleModal(translate(['ERRORS', 'PAYMENT']));
      setContentModal(<ErrorMessage error={errorObject as ErrorObject} />);
      toggleModal({ open : true });

      setTimeout(() => {
        navigateToFirstRoute();
      }, 2000);
    }
  };

  const validateCart = async ({
    user,
    event,
  }: {
    user: User;
    event: Event;
    navigate: NavigateFunction
  }): Promise<boolean> => {
    setCartLoading(true);
    if (Producers.Bahia.includes(producerId)) {
      const checkTicketCategory = (categoryIds: string[]) =>
        cart.some((item) =>
          item.type.some((type) => categoryIds.includes(type.category?.id ?? ''))
        );

      const isHalfEntry = checkTicketCategory([String(TicketTypes.HALF_ENTRY)]);
      const isFanMember = checkTicketCategory([String(TicketTypes.FAN_MEMBER), String(TicketTypes.CHECKIN_MEMBER)]);

      if (isHalfEntry) {
        const isArenaMember = await verifyHalfEntry(user.document.number, producerId);
        if (!isArenaMember) {
          setTimeout(() => {
            defineTitleModal(
              translate(['INFORMATION', 'MEIA_ENTRADA_NOT_FOUND_TITLE'])
            );
            setContentModal(<ValidateMeiaEntrada isNotFound />);
            toggleModal({ open : true });
          }, 700);
          setCartLoading(false);
          handleAuthenticatedUser(false);
          return false;
        }
      }

      if (isFanMember && membershipApplied && removeDocumentMask(membershipApplied.document) !== removeDocumentMask(user.document.number)) {
        changeMembershipApplied(null);
        getGetTickets({ event });

        const deleteTicketFromCategory = [
          String(TicketTypes.FAN_MEMBER),
          String(TicketTypes.CHECKIN_MEMBER),
        ];

        cart.forEach((ticket) => {
          ticket.type.forEach((type) => {
            if (deleteTicketFromCategory.includes(type.category?.id)) {
              removeTicketFromCart({ groupId : ticket.id, ticketId : type.id });
            }
          });
        });

        setTimeout(() => {
          defineTitleModal(translate(['INFORMATION', 'RESTRICTED_TICKET_TITLE']));
          setContentModal(<ValidateMembership />);
          toggleModal({ open : true });
        }, 1000);

        setCartLoading(false);
        handleAuthenticatedUser(false);
        return false;
      }
    }
    const apiKey = Environment.API_KEY;
    const userToken = getIngCookieValue('token')!;
    const resourcePath = `/shop/validate?apikey=${apiKey}&usertoken=${userToken}`;
    const data = prepareCheckoutData({ namingData, user, event });
    toggleLoader({ show : true });
    try {
      const { detail } = await request<
        Checkout,
        { detail: { message: string } }
      >(
        {
          requestOptions : {
            resourcePath,
            data : {
              ...data,
              ...(passKeyApplied && { passkey : passKeyApplied }),
              userId : user.id,
              ticketOwnerDocument : null,
              tickets : data.tickets.map((ticket) => ({
                ...ticket,
                holder : [],
              })),
            } as Checkout,
          },
          type : HttpVerb.POST,
        },
        { baseUrl : Environment.API_TICKETS_URL, needToken : true }
      );

      const isValidAssociations = await getTicketsInMyWallet(user.id, namingData);
      if (!isValidAssociations) {
        setTimeout(() => {
          defineTitleModal(translate(['INFORMATION', 'TICKET_SELECTION_ERROR_TITLE']));
          setContentModal(translate(['INFORMATION', 'ASSOCIATION_ERROR_FACE_RECOGNITION_SELECTION']));
          toggleModal({ open : true });
        }, 700);
        handleAuthenticatedUser(false);
        return false;
      }

      return detail.message === 'success';
    } catch (error) {
      const data = error as { response: { data: { code: string; message: string; context: { unavailableTickets: number[] } } } };
      setErrorValidating({
        code : data.response.data.code,
        error : getErrorMessage(data.response.data.code, locale, data.response.data.message),
      });

      const errorHandlers = createErrorHandlers(
        locale,
        handleSetErrorsInSummary,
        defineTitleModal,
        getErrorMessage,
        translate
      );

      setTimeout(() => {
        const code = data.response.data.code;

        const handler = errorHandlers.find((h) => h.condition(code))?.handle;
        handler && handler(data);

        setContentModal(<ErrorMessage error={data.response?.data as ErrorObject} />);
        toggleModal({ open : true });
      }, 700);

      toggleLoader({ show : false });
      return false;
    } finally {
      toggleLoader({ show : false });
      setCartLoading(false);
    }
  };

  const updateNamingDataList = (namingDataList: AssignmentTickets[]) => {
    setNamingData(namingDataList);
    updateBlockedSessionsForMe(namingDataList);
  };

  const justOneAssignPerSession = (updatedNamingData: AssignmentTickets[]) => {
    let sessionsIds = [] as number[];
    updatedNamingData.forEach((namingData) => {
      if (namingData.holder?.id === user?.id) {
        sessionsIds = [...sessionsIds, ...namingData.dates.map(({ id }) => id)];
      }
    });

    return sessionsIds;
  };

  const updateBlockedSessionsForMe = (expectedNamingData: AssignmentTickets[], isRemove = false) => {
    const sessionsIdsToBlock = justOneAssignPerSession(expectedNamingData);

    if (isRemove) {
      const filteredBlockedSessionsForMe = blockedSessionsForMe.filter((curSession) => sessionsIdsToBlock.includes(curSession));

      setBlockedSessionsForMe(filteredBlockedSessionsForMe);
      return;
    }

    setBlockedSessionsForMe((state) => [...state, ...sessionsIdsToBlock]);
  };

  /**
   * Stringified absences ('undefined' / 'null') are normalized to '' so they never
   * reach the context. `trackPurchase` rejects those literals, so storing one would
   * silently drop the analytics for a purchase that actually went through.
   */
  const updateTransactionId = (id: string) => {
    const normalized = typeof id === 'string' ? id.trim() : '';
    setTransactionId(
      !normalized || normalized === 'undefined' || normalized === 'null' ? '' : normalized
    );
  };

  const updatePaymentType = (type: string | null) => {
    setPaymentType(type);
  };

  const resetCart = (preserveTransactionId = false) => {
    setCart([]);
    setNamingData([]);
    setSummaryData([]);
    if (!preserveTransactionId) {
      setTransactionId(null);
      setPaymentType(null);
    }
    setCombosInCart([]);
    setCombosInNaming([]);
    setTicketsPerSession({} as TicketsPerSession);
    setBlockedSessionsForMe([]);
    setTotalTicketsInCart(0);
    setErrorValidating({
      code : null,
      error : null,
    });
  };

  const updateComboInCart = (comboData : ComboData, remove = false) => {
    if (remove) {
      const newCombosInCart = combosInCart.filter(({ id }) => id !== comboData.id);
      setCombosInCart(newCombosInCart);
      return;
    }

    const hasSameComboDates = (firstCombo: ComboData, secondCombo: ComboData) => {
      if (firstCombo.id !== secondCombo.id) return false;
      if (firstCombo.comboDates.length !== secondCombo.comboDates.length) return false;

      // Sort combo dates by id to ensure consistent comparison
      const sortedComboDatesFromFirstCombo = [...firstCombo.comboDates].sort((a, b) => a.id - b.id);
      const sortedComboDatesFromSecondCombo = [...secondCombo.comboDates].sort((a, b) => a.id - b.id);

      return sortedComboDatesFromFirstCombo.every((comboDateFromFirst, dateIndex) => {
        const comboDateFromSecond = sortedComboDatesFromSecondCombo[dateIndex];
        if (comboDateFromFirst.id !== comboDateFromSecond.id) return false;

        // Compare selected tickets in each date
        const selectedTicketIdFromFirstCombo = comboDateFromFirst.groups[0]?.tickets[0]?.id;
        const selectedTicketIdFromSecondCombo = comboDateFromSecond.groups[0]?.tickets[0]?.id;

        return selectedTicketIdFromFirstCombo === selectedTicketIdFromSecondCombo;
      });
    };

    const alreadyExistsThisComboInCart = combosInCart.some((combo) =>
      hasSameComboDates(combo, comboData)
    );

    if (alreadyExistsThisComboInCart) {
      const comboInCartWithUpdatedQuantity = combosInCart.map((combo) => {
        if (hasSameComboDates(combo, comboData)) {
          return { ...combo, quantity : combo.quantity + 1 };
        }
        return combo;
      });

      return setCombosInCart(comboInCartWithUpdatedQuantity);
    }

    setCombosInCart((state) => [...state, { ...comboData, quantity : 1 }]);
  };

  const createComboInNaming = () => {
    let expectedComboInNaming = [] as ComboInNaming[];

    combosInCart.forEach((combo) => {
      for (let index = 0; index < combo.quantity; index++) {
        const preSetterHolder = combosInCart.length === 1 && combo.quantity === 1;
        expectedComboInNaming = [
          ...expectedComboInNaming,
          {
            ...combo,
            quantity : 1, // Each entry in combosInNaming represents 1 individual combo
            holder : preSetterHolder ? ({
              id : Number(user?.id),
              email : String(user?.email),
              name : String(user?.name),
              picture : user?.pictures?.[0].link ?? avatarDefaultUrl,
            }) : null,
            isTransferableGuestType : true,
            isDependents : false,
            faceRecognition : false,
            transferMandatoryFaceRecognition : false,
            idSpecificInIdentifyList : uuidv4(),
          },
        ];
      }
    });

    setCombosInNaming(expectedComboInNaming);
  };

  const updateCombosInNaming = (updatedCombosInNaming: ComboInNaming[]) => {
    setCombosInNaming(updatedCombosInNaming);
  };

  useEffect(() => {
    setNamingData(prepareTicketsForAssignment());
  }, [cart, event]);

  /**
   * useEffect hook that triggers the `removeTicketsWithPasskey` function
   * whenever the `passKeyApplied` state changes.
   */
  useEffect(() => {
    applyCartRules({
      event : event!,
      tickets,
      cart,
      couponApplied,
      passKeyApplied,
      updateCart : setCart,
      updateSummaryData : prepareSummaryData,
      membership : membershipApplied,
      updateTicketsPerSession : removeTicketsFromTicketsPerSession,
    });
  }, [passKeyApplied]);

  /**
   * useEffect hook to handle side effects when the tickets or cart state changes.
   * This hook runs whenever the `tickets` or `cart` array changes and performs the following actions:
   * - If either the tickets or cart is empty, it updates the total tickets in the cart and prepares the summary data.
   * - If both the tickets and cart are not empty, it updates the prices in the cart if a coupon is applied.
   * @param tickets - The array of ticket objects.
   * @param cart - The array of items in the cart.
   */
  useEffect(() => {
    if (tickets.length === 0 || cart.length === 0) {
      setTotalTicketsInCart(getTotalTicketsInCart());
      prepareSummaryData({ newCart : cart });
      return;
    }
    updatePriceInCartIfCouponApplied();
  }, [tickets]);

  const shared = {
    combosInNaming,
    updateCombosInNaming,
    combosInCart,
    blockedSessionsForMe,
    cart,
    cartLoading,
    namingData,
    summaryData,
    errorValidating,
    totalTicketsInCart,
    validateCart,
    addTicketToCart,
    prepareCheckoutData,
    removeTicketFromCart,
    assignTicketToHolder,
    getTotalTicketsInCart,
    removeSelectedTickets,
    getTotalTicketsByGroup,
    associateTheTicketForMe,
    requestCheckoutPaymentUrl,
    checkQuantityIfAddedInCart,
    invalidateAssignTicketToHolder,
    updateNamingDataList,
    updateBlockedSessionsForMe,
    transactionId,
    updateTransactionId,
    paymentType,
    updatePaymentType,
    resetCart,
    ticketsPerSession,
    updateComboInCart,
    createComboInNaming,
    prepareCheckoutComboData,
  };

  return <CartContext.Provider value={shared}>{children}</CartContext.Provider>;
}

CartProvider.displayName = 'CartProvider';
export const useCartContext = (): CartContextProps => useContext(CartContext);

export { CartContext, CartProvider };
